import type {
    CurrentRefinementsConnectorParamsItem,
    CurrentRefinementsConnectorParamsRefinement,
} from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

import {
    denormalizeFilterName,
    getDefaultCategories,
    getMappedCategoryName,
    type ProductCategory,
    type ProductHit,
    transformHitForProductTile,
} from '@libs/algolia';
import {
    type Product as AnalyticsProduct,
    createItemsViewEvent,
    dataLayerPush,
    Events,
    trackEEImpression,
    trackProductTileItemClick,
} from '@libs/analytics';
import type {
    MultiProductPromotionResponse,
    MultiProductPromotionResponseItem,
    Product,
    ProductPromotion,
} from '@libs/common-next';
import { getNextApiRoute, isUserEligibleForPromo, typedFetch } from '@libs/common-next';
import { HttpMethod, toTitleCase } from '@libs/core';

import {
    getExistingSubscriptionForViewItemList,
    type ViewItemListAutoshipContext,
} from '@/components/ProductGrid/model/getExistingSubscriptionForViewItemList';

const getPromotions = async (
    productId: string,
    signal: AbortSignal
): Promise<Nullable<MultiProductPromotionResponse>> =>
    await typedFetch<MultiProductPromotionResponse>(getNextApiRoute(`promotions?ids=${productId}`), {
        headers: {
            accept: 'application/json',
            'content-type': 'application/json',
        },
        method: HttpMethod.GET,
        signal,
    });

export const updateProductsWithPromotions = (
    product: Product,
    promotionsToUse: MultiProductPromotionResponseItem[],
    isAuthenticated: boolean
): Product => {
    const productPromotions = promotionsToUse.find(
        (promotionList: MultiProductPromotionResponseItem) => +promotionList.id === product.masterProductID
    );
    if (productPromotions && productPromotions.promotions && productPromotions.promotions.length > 0) {
        return {
            ...product,
            promos: productPromotions.promotions
                .filter(promo => isUserEligibleForPromo(promo, isAuthenticated))
                .map((promo: ProductPromotion) => ({
                    title: promo.callout_msg,
                    description: '',
                })),
        };
    }

    return product;
};

export const getProductsPromotions = async (
    products: Product[],
    setProductsAndPromotions: (products: Product[]) => void,
    signal: AbortSignal,
    signal2: AbortSignal,
    isAuthenticated: boolean
) => {
    // need to split into two calls, API only supports a limited amount of productIds at a time
    const productList1 = products
        .slice(0, 23)
        .map(p => p.masterProductID)
        .join(',');
    const productList2 = products
        .slice(23)
        .map(p => p.masterProductID)
        .join(',');

    const promises: Promise<Nullable<MultiProductPromotionResponse>>[] = [];
    promises.push(getPromotions(productList1, signal));
    if (productList2) {
        promises.push(getPromotions(productList2, signal2));
    }

    const response = await Promise.all(promises);

    let allPromotions: MultiProductPromotionResponseItem[] = [];
    response.forEach((r: Nullable<MultiProductPromotionResponse>) => {
        if (r && r.success && r.products.length > 0) {
            allPromotions = allPromotions.concat(r.products);
        }
    });
    const allProducts: Product[] = products.map((p: Product) =>
        updateProductsWithPromotions(p, allPromotions, isAuthenticated)
    );
    setProductsAndPromotions(allProducts);
};

export function trackPlpProductTileItemClick(
    productHit: ProductHit,
    {
        isSearchPage,
        searchTerm,
        viewItemListAutoshipContext,
    }: {
        isSearchPage: boolean;
        searchTerm: string | undefined;
        viewItemListAutoshipContext: ViewItemListAutoshipContext;
    }
): void {
    const hit = transformHitForProductTile(productHit) as Product;
    const filteredCategories = getDefaultCategories(productHit).map((p: ProductCategory) => p.name.default);
    const resolveExistingSubscription = (product: Product) =>
        getExistingSubscriptionForViewItemList(product, viewItemListAutoshipContext);

    const itemsViewPayload = createItemsViewEvent(
        [hit],
        isSearchPage ? 'search' : 'main navigation',
        isSearchPage ? 'search' : 'standard plp',
        filteredCategories,
        searchTerm,
        resolveExistingSubscription
    );

    trackProductTileItemClick({
        product: hit as unknown as AnalyticsProduct,
        ecommerce: itemsViewPayload.ecommerce,
    });
}

export const pushEvent = (
    productsToSend: Product[],
    isSearchPage: boolean,
    currentCategories: CurrentRefinementsConnectorParamsItem,
    searchWord?: string,
    viewItemListAutoshipContext?: ViewItemListAutoshipContext
) => {
    const categories = currentCategories
        ? currentCategories.refinements.map((c: CurrentRefinementsConnectorParamsRefinement) => c.value.toString())
        : [];
    const splitCategories = categories.length > 0 ? categories[0].split(' > ') : [];

    const resolveExistingSubscription = viewItemListAutoshipContext
        ? (product: Product) => getExistingSubscriptionForViewItemList(product, viewItemListAutoshipContext)
        : undefined;

    if (productsToSend.length > 0) {
        dataLayerPush({
            event: Events.VIEW_ITEM_LIST,
            ...createItemsViewEvent(
                productsToSend,
                isSearchPage ? 'search' : 'main navigation',
                isSearchPage ? 'search' : 'standard plp',
                splitCategories,
                searchWord ?? undefined,
                resolveExistingSubscription
            ),
        });

        const list = isSearchPage ? 'Search Results' : 'PLP Page';
        trackEEImpression({
            products: productsToSend.map(({ rawProductHit }) => rawProductHit),
            list,
        });
    }
};

export const stringifyFilters = (filters: CurrentRefinementsConnectorParamsItem[]) =>
    filters
        .map(
            (r: CurrentRefinementsConnectorParamsItem) =>
                `${r.attribute}${r.refinements.map((f: CurrentRefinementsConnectorParamsRefinement) => f.value)}`
        )
        .toString();

export const slugToTitle = (slug?: string) => {
    if (!slug) {
        return '';
    }

    const words = slug.split('/').pop()?.split('-') ?? [];
    const titleArray: string[] = [];

    for (let index = 0; index < words.length; index++) {
        const word = words[index];
        const next = words[index + 1];

        // The category map key is "T shirts and Tank Tops".
        if (/^[A-Za-z]$/.test(word) && next && next !== 'and') {
            titleArray.push(`${word.toUpperCase()} ${next.toLowerCase()}`);
            index++;
            continue;
        }

        titleArray.push(word === 'and' ? word : toTitleCase(word));
    }

    return titleArray.join(' ');
};

export const formatCategoryHeading = (slug?: string) => {
    const title = slugToTitle(slug);
    const mapped = getMappedCategoryName(title);

    return mapped === title ? denormalizeFilterName(title) : mapped;
};
